import React, { useRef, useState } from 'react'
import {
    Button,
    Checkbox,
    CircularProgress,
    Divider,
    IconButton,
    Modal,
    Paper,
    Slide,
    TextField,
    Tooltip,
    useTheme,
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import CloseIcon from '@material-ui/icons/Close'
import DeleteIcon from '@material-ui/icons/Delete'
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'
import GetAppIcon from '@material-ui/icons/GetApp'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import PublishIcon from '@material-ui/icons/Publish'
import { Container } from '../../components'
import { useUser } from '../../hooks'
import { RootState } from '../../store'
import { CREATE_USER_RESPONSE } from '../../store/user/types'
import {
    isEmailValid,
    emailHelperText,
    isAccountManager,
    axiosInstance,
} from '../../helpers'
import { typographyStyle } from '../../theme/typographyStyle'
import { toast } from 'react-toastify'
import { useDispatch, useSelector } from 'react-redux'
import {
    EDIT_USER,
    EDIT_USER_PERMISSIONS,
    PERMISSION_TRUE,
    PermissionRole,
} from '../../models/Permissions'
import { hasPermission } from '../../models/Users/services'

interface UserRow {
    id: number
    name: string
    email: string
    phone: string
    cellPhone: string
    expirationDate: string
    status: 'idle' | 'loading' | 'success' | 'error'
    errorMessage?: string
}

type RowField = 'name' | 'email' | 'phone' | 'cellPhone' | 'expirationDate'

interface Props {
    open: boolean
    handleClose: () => void
}

let nextId = 1

const createEmptyRow = (): UserRow => ({
    id: nextId++,
    name: '',
    email: '',
    phone: '',
    cellPhone: '',
    expirationDate: '',
    status: 'idle',
})

const TEMPLATE_HEADERS = [
    'Name (Required)',
    'Email (Required)',
    'Phone',
    'Cell Phone',
    'Expiration Date (YYYY-MM-DD)',
]

const downloadTemplate = () => {
    const csv = TEMPLATE_HEADERS.join(',') + '\n'
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'bulk_add_members_template.csv'
    a.click()
    URL.revokeObjectURL(url)
}

const parseCSV = (text: string): Omit<UserRow, 'id' | 'status'>[] => {
    const lines = text
        .split(/\r?\n/)
        .map((l) => l.trim())
        .filter(Boolean)

    if (lines.length < 2) return []

    // strip quoted values and split on commas
    const parseLine = (line: string): string[] => {
        const result: string[] = []
        let current = ''
        let inQuotes = false
        for (let i = 0; i < line.length; i++) {
            const ch = line[i]
            if (ch === '"') {
                inQuotes = !inQuotes
            } else if (ch === ',' && !inQuotes) {
                result.push(current.trim())
                current = ''
            } else {
                current += ch
            }
        }
        result.push(current.trim())
        return result
    }

    const header = parseLine(lines[0]).map((h) => h.toLowerCase())
    const nameIdx = header.findIndex((h) => h.includes('name'))
    const emailIdx = header.findIndex((h) => h.includes('email'))
    const phoneIdx = header.findIndex(
        (h) => h.includes('phone') && !h.includes('cell'),
    )
    const cellIdx = header.findIndex((h) => h.includes('cell'))
    const expirationIdx = header.findIndex(
        (h) => h.includes('expir') || h.includes('archive'),
    )

    return lines
        .slice(1)
        .map((line) => {
            const cols = parseLine(line)
            return {
                name: nameIdx >= 0 ? cols[nameIdx] ?? '' : '',
                email: emailIdx >= 0 ? cols[emailIdx] ?? '' : '',
                phone: phoneIdx >= 0 ? cols[phoneIdx] ?? '' : '',
                cellPhone: cellIdx >= 0 ? cols[cellIdx] ?? '' : '',
                expirationDate:
                    expirationIdx >= 0 ? cols[expirationIdx] ?? '' : '',
            }
        })
        .filter((r) => r.name || r.email)
}

export const BulkCreateUsersModal = ({ open, handleClose }: Props) => {
    const theme = useTheme()
    const dispatch = useDispatch()
    const { workspaceUser } = useUser()
    const uploadRef = useRef<HTMLInputElement>(null)

    const permissionRoles = useSelector(
        (state: RootState) => state.permission.permissionRoleList,
    )

    // Permission checks — mirrors UserModal logic
    const canEditUsers = hasPermission(workspaceUser, EDIT_USER)
    const canEditPermissions = hasPermission(
        workspaceUser,
        EDIT_USER_PERMISSIONS,
    )

    const [rows, setRows] = useState<UserRow[]>([createEmptyRow()])
    const [doValidate, setDoValidate] = useState(false)
    const [isBulkLoading, setIsBulkLoading] = useState(false)
    const [selectedRoles, setSelectedRoles] = useState<number[]>([])

    const addRow = () => setRows((prev) => [...prev, createEmptyRow()])

    const removeRow = (id: number) =>
        setRows((prev) => prev.filter((r) => r.id !== id))

    const updateRow = (id: number, field: RowField, value: string) => {
        setRows((prev) =>
            prev.map((r) =>
                r.id === id
                    ? {
                          ...r,
                          [field]: value,
                          status: 'idle',
                          errorMessage: undefined,
                      }
                    : r,
            ),
        )
    }

    const setRowStatus = (
        id: number,
        status: UserRow['status'],
        errorMessage?: string,
    ) => {
        setRows((prev) =>
            prev.map((r) => (r.id === id ? { ...r, status, errorMessage } : r)),
        )
    }

    const toggleRole = (roleId: number) => {
        setSelectedRoles((prev) =>
            prev.includes(roleId)
                ? prev.filter((id) => id !== roleId)
                : [...prev, roleId],
        )
    }

    const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file) return
        const reader = new FileReader()
        reader.onload = (ev) => {
            const text = ev.target?.result as string
            const parsed = parseCSV(text)
            if (parsed.length === 0) {
                toast.error(
                    'No rows found. Make sure the file matches the template format.',
                )
                return
            }
            const newRows: UserRow[] = parsed.map((r) => ({
                ...r,
                id: nextId++,
                status: 'idle',
            }))
            setRows(newRows)
            setDoValidate(false)
        }
        reader.readAsText(file)
        // reset so the same file can be re-uploaded
        e.target.value = ''
    }

    const handleCreate = async () => {
        setDoValidate(true)

        const pendingRows = rows.filter((r) => r.status !== 'success')
        const hasErrors = pendingRows.some(
            (r) => r.name.trim() === '' || !isEmailValid(r.email),
        )
        if (hasErrors) return

        setIsBulkLoading(true)

        const payrollRoleIds = new Set(
            (permissionRoles ?? [])
                .filter((r) => r.payroll_access === PERMISSION_TRUE)
                .map((r) => r.id),
        )
        const canAssignPayroll = isAccountManager(workspaceUser)
        const roleIds = canAssignPayroll
            ? selectedRoles
            : selectedRoles.filter((id) => !payrollRoleIds.has(id))

        // Mark all pending rows as loading
        pendingRows.forEach((row) => setRowStatus(row.id, 'loading'))

        let successCount = 0

        try {
            const response = await axiosInstance.post('user/bulk_create/', {
                users: pendingRows.map((row) => ({
                    name: row.name.trim(),
                    email: row.email.trim(),
                    phone: row.phone.trim(),
                    cell_phone: row.cellPhone.trim(),
                    permission_roles: roleIds,
                    auto_archive_date: row.expirationDate || null,
                })),
            })

            const apiResults: Array<{
                email: string
                success: boolean
                user?: any
                error?: string
            }> = response.data.results

            // Map results back to rows by index (order is preserved)
            pendingRows.forEach((row, i) => {
                const result = apiResults[i]
                if (result?.success) {
                    setRowStatus(row.id, 'success')
                    successCount++
                    // Keep Redux user list in sync
                    if (result.user) {
                        dispatch({
                            type: CREATE_USER_RESPONSE,
                            payload: result.user,
                        })
                    }
                } else {
                    setRowStatus(
                        row.id,
                        'error',
                        result?.error ?? 'Failed to create user',
                    )
                }
            })
        } catch (e: any) {
            // Network-level failure — mark all pending rows as failed
            const msg =
                e?.response?.data?.message ??
                'Request failed. Please try again.'
            pendingRows.forEach((row) => setRowStatus(row.id, 'error', msg))
            toast.error(msg)
        }

        setIsBulkLoading(false)

        if (successCount > 0) {
            toast.success(
                `${successCount} user${
                    successCount > 1 ? 's' : ''
                } created successfully!`,
            )
        }

        if (successCount === pendingRows.length) {
            onClose()
        }
    }

    const onClose = () => {
        setRows([createEmptyRow()])
        setDoValidate(false)
        setIsBulkLoading(false)
        setSelectedRoles([])
        handleClose()
    }

    const pendingRows = rows.filter((r) => r.status !== 'success')
    const successRows = rows.filter((r) => r.status === 'success')

    // Mirrors UserModal: disableCheckbox when user lacks EDIT_USER_PERMISSIONS
    const disableCheckbox = !canEditPermissions

    const renderPermissionRoles = (roles: PermissionRole[] | undefined) =>
        roles?.map((role) => {
            const hasPayrollAccess = role.payroll_access === PERMISSION_TRUE
            const isRoleDisabled =
                hasPayrollAccess && !isAccountManager(workspaceUser)
            const isChecked = selectedRoles.includes(role.id)
            const isDisabled =
                disableCheckbox || isRoleDisabled || isBulkLoading
            return (
                <Tooltip
                    key={role.id}
                    title={
                        isRoleDisabled
                            ? 'Only Account managers can assign roles with Payroll Access'
                            : disableCheckbox
                            ? 'You do not have permission to assign roles'
                            : ''
                    }
                >
                    <Container
                        style={{
                            alignItems: 'center',
                            cursor: isDisabled ? 'default' : 'pointer',
                            borderRadius: theme.shape.borderRadius,
                            padding: theme.spacing(0.25, 0.5),
                        }}
                        onClick={() => {
                            if (!isDisabled) toggleRole(role.id)
                        }}
                    >
                        <Container style={{ flex: 1, fontSize: 14 }}>
                            {role.name}
                        </Container>
                        <Checkbox
                            checked={isChecked}
                            disabled={isDisabled}
                            style={{
                                color: isDisabled
                                    ? undefined
                                    : isChecked
                                    ? theme.palette.primary.dark
                                    : undefined,
                                padding: theme.spacing(0.5),
                            }}
                            onClick={(e) => e.stopPropagation()}
                            onChange={() => {
                                if (!isDisabled) toggleRole(role.id)
                            }}
                        />
                    </Container>
                </Tooltip>
            )
        })

    const colHeaderStyle: React.CSSProperties = {
        flex: 1,
        fontWeight: 600,
        fontSize: 13,
        color: theme.palette.text.secondary,
        paddingLeft: theme.spacing(1),
    }

    return (
        <Modal
            open={open}
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
            onClose={onClose}
        >
            <Slide direction="up" in={open}>
                <Paper
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        outline: 'none',
                        borderRadius: '20px',
                        width: 'min(1280px, 96vw)',
                        height: '90vh',
                        overflow: 'hidden',
                    }}
                >
                    {/* ── Header ── */}
                    <Container
                        style={{
                            padding: theme.spacing(3, 3, 2, 3),
                            alignItems: 'center',
                            flexShrink: 0,
                        }}
                    >
                        <span
                            style={{
                                ...typographyStyle(theme.typography.h5),
                                fontWeight: theme.typography.fontWeightBold,
                                marginLeft: theme.spacing(1),
                            }}
                        >
                            Bulk Add Members
                        </span>
                        <div style={{ flex: 1 }} />

                        {/* Template + Upload actions */}
                        <Tooltip title="Download CSV template">
                            <Button
                                startIcon={<GetAppIcon />}
                                onClick={downloadTemplate}
                                disabled={isBulkLoading}
                                style={{
                                    textTransform: 'none',
                                    color: theme.palette.text.secondary,
                                    marginRight: theme.spacing(1),
                                }}
                                size="small"
                            >
                                Template
                            </Button>
                        </Tooltip>

                        <Tooltip
                            title={
                                !canEditUsers
                                    ? 'You do not have permission to add users'
                                    : 'Upload a filled-out CSV to populate the list'
                            }
                        >
                            <span>
                                <Button
                                    startIcon={<PublishIcon />}
                                    onClick={() => uploadRef.current?.click()}
                                    disabled={isBulkLoading || !canEditUsers}
                                    style={{
                                        textTransform: 'none',
                                        color: '#008C85',
                                        marginRight: theme.spacing(2),
                                    }}
                                    size="small"
                                >
                                    Upload CSV
                                </Button>
                            </span>
                        </Tooltip>

                        <Tooltip title="Cancel">
                            <IconButton onClick={onClose}>
                                <CloseIcon fontSize="large" />
                            </IconButton>
                        </Tooltip>
                    </Container>

                    <Divider
                        style={{ margin: theme.spacing(0, 3), flexShrink: 0 }}
                    />

                    <Container
                        style={{
                            padding: theme.spacing(1, 3),
                            color: theme.palette.text.secondary,
                            fontSize: 14,
                            flexShrink: 0,
                        }}
                    >
                        Fill in each row manually or upload a CSV using the
                        template above. Permission roles on the right apply to
                        all members.
                    </Container>

                    {!canEditUsers && (
                        <Container
                            style={{
                                margin: theme.spacing(0, 3, 1, 3),
                                padding: theme.spacing(1, 2),
                                backgroundColor: theme.palette.grey[100],
                                borderRadius: theme.shape.borderRadius,
                                border: `1px solid ${theme.palette.divider}`,
                                fontSize: 13,
                                color: theme.palette.text.secondary,
                                flexShrink: 0,
                            }}
                        >
                            You do not have permission to add users. Contact
                            your workspace admin.
                        </Container>
                    )}

                    {/* ── Body — two columns ── */}
                    <Container
                        style={{
                            padding: theme.spacing(0, 3),
                            gap: theme.spacing(3),
                            alignItems: 'flex-start',
                            overflow: 'hidden',
                            flex: 1,
                            minHeight: 0,
                        }}
                    >
                        {/* Left — user rows */}
                        <Container
                            direction="column"
                            style={{
                                flex: 1,
                                overflow: 'hidden',
                                minHeight: 0,
                            }}
                        >
                            {/* Column headers */}
                            <Container
                                style={{
                                    padding: theme.spacing(1, 0, 0.5, 0),
                                    gap: theme.spacing(1),
                                    flexShrink: 0,
                                }}
                            >
                                <span
                                    style={{
                                        ...colHeaderStyle,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 4,
                                    }}
                                >
                                    Name
                                    <span
                                        style={{
                                            fontSize: 10,
                                            fontWeight: 700,
                                            color: theme.palette.error.main,
                                            letterSpacing: '0.04em',
                                        }}
                                    >
                                        REQUIRED
                                    </span>
                                </span>
                                <span
                                    style={{
                                        ...colHeaderStyle,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 4,
                                    }}
                                >
                                    Email
                                    <span
                                        style={{
                                            fontSize: 10,
                                            fontWeight: 700,
                                            color: theme.palette.error.main,
                                            letterSpacing: '0.04em',
                                        }}
                                    >
                                        REQUIRED
                                    </span>
                                </span>
                                <span style={colHeaderStyle}>Phone</span>
                                <span style={colHeaderStyle}>Cell Phone</span>
                                <span
                                    style={{
                                        ...colHeaderStyle,
                                        flex: 0.9,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 4,
                                    }}
                                >
                                    Expiration Date
                                    <Tooltip title="The date this member's access will automatically expire. Leave blank for no expiration.">
                                        <InfoOutlinedIcon
                                            style={{
                                                fontSize: 14,
                                                color:
                                                    theme.palette.text
                                                        .secondary,
                                                cursor: 'default',
                                            }}
                                        />
                                    </Tooltip>
                                </span>
                                {/* spacer for status + delete */}
                                <div style={{ width: 80 }} />
                            </Container>

                            {/* Scrollable rows */}
                            <div
                                style={{
                                    overflowY: 'auto',
                                    maxHeight: 'calc(90vh - 260px)',
                                }}
                            >
                                {rows.map((row) => {
                                    const nameError =
                                        doValidate && row.name.trim() === ''
                                    const emailError =
                                        doValidate && !isEmailValid(row.email)
                                    const locked =
                                        !canEditUsers ||
                                        row.status === 'loading' ||
                                        row.status === 'success'

                                    return (
                                        <Container
                                            key={row.id}
                                            style={{
                                                alignItems: 'flex-start',
                                                gap: theme.spacing(1),
                                                paddingTop: theme.spacing(0.5),
                                                paddingBottom: theme.spacing(
                                                    0.5,
                                                ),
                                                opacity:
                                                    row.status === 'success'
                                                        ? 0.55
                                                        : 1,
                                            }}
                                        >
                                            <TextField
                                                variant="outlined"
                                                size="small"
                                                value={row.name}
                                                onChange={(e) =>
                                                    updateRow(
                                                        row.id,
                                                        'name',
                                                        e.target.value,
                                                    )
                                                }
                                                error={nameError}
                                                helperText={
                                                    nameError ? 'Required' : ''
                                                }
                                                style={{ flex: 1 }}
                                                disabled={locked}
                                            />
                                            <TextField
                                                variant="outlined"
                                                size="small"
                                                value={row.email}
                                                onChange={(e) =>
                                                    updateRow(
                                                        row.id,
                                                        'email',
                                                        e.target.value,
                                                    )
                                                }
                                                error={
                                                    emailError ||
                                                    row.status === 'error'
                                                }
                                                helperText={
                                                    row.status === 'error'
                                                        ? row.errorMessage
                                                        : emailHelperText(
                                                              doValidate,
                                                              row.email,
                                                          )
                                                }
                                                style={{ flex: 1 }}
                                                disabled={locked}
                                            />
                                            <TextField
                                                variant="outlined"
                                                size="small"
                                                value={row.phone}
                                                onChange={(e) =>
                                                    updateRow(
                                                        row.id,
                                                        'phone',
                                                        e.target.value,
                                                    )
                                                }
                                                style={{ flex: 1 }}
                                                disabled={locked}
                                            />
                                            <TextField
                                                variant="outlined"
                                                size="small"
                                                value={row.cellPhone}
                                                onChange={(e) =>
                                                    updateRow(
                                                        row.id,
                                                        'cellPhone',
                                                        e.target.value,
                                                    )
                                                }
                                                style={{ flex: 1 }}
                                                disabled={locked}
                                            />
                                            <TextField
                                                variant="outlined"
                                                size="small"
                                                type="date"
                                                value={row.expirationDate}
                                                onChange={(e) =>
                                                    updateRow(
                                                        row.id,
                                                        'expirationDate',
                                                        e.target.value,
                                                    )
                                                }
                                                style={{ flex: 0.9 }}
                                                disabled={locked}
                                                InputLabelProps={{
                                                    shrink: true,
                                                }}
                                            />

                                            {/* Status icon */}
                                            <Container
                                                style={{
                                                    width: 32,
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                    paddingTop: theme.spacing(
                                                        0.5,
                                                    ),
                                                }}
                                            >
                                                {row.status === 'loading' && (
                                                    <CircularProgress
                                                        size={18}
                                                    />
                                                )}
                                                {row.status === 'success' && (
                                                    <CheckCircleOutlineIcon
                                                        style={{
                                                            color: '#008C85',
                                                            fontSize: 20,
                                                        }}
                                                    />
                                                )}
                                                {row.status === 'error' && (
                                                    <ErrorOutlineIcon
                                                        color="error"
                                                        style={{ fontSize: 20 }}
                                                    />
                                                )}
                                            </Container>

                                            <Tooltip title="Remove row">
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() =>
                                                            removeRow(row.id)
                                                        }
                                                        disabled={
                                                            rows.length === 1 ||
                                                            locked
                                                        }
                                                        style={{
                                                            marginTop: theme.spacing(
                                                                0.5,
                                                            ),
                                                        }}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </Container>
                                    )
                                })}

                                <Container
                                    style={{
                                        paddingTop: theme.spacing(0.5),
                                        paddingBottom: theme.spacing(1),
                                    }}
                                >
                                    <Button
                                        startIcon={<AddIcon />}
                                        onClick={addRow}
                                        disabled={
                                            isBulkLoading || !canEditUsers
                                        }
                                        style={{
                                            textTransform: 'none',
                                            color: '#008C85',
                                        }}
                                    >
                                        Add another
                                    </Button>
                                </Container>
                            </div>
                        </Container>

                        {/* Right — permission roles */}
                        <Container
                            direction="column"
                            style={{
                                width: 260,
                                flexShrink: 0,
                                minHeight: 0,
                                overflow: 'hidden',
                                borderLeft: `1px solid ${theme.palette.divider}`,
                                paddingLeft: theme.spacing(2),
                                paddingTop: theme.spacing(1),
                            }}
                        >
                            <Container
                                style={{
                                    fontWeight: theme.typography.fontWeightBold,
                                    fontSize: 15,
                                    marginBottom: theme.spacing(0.5),
                                    flexShrink: 0,
                                }}
                            >
                                Permission Roles
                            </Container>
                            <Container
                                style={{
                                    fontSize: 12,
                                    color: theme.palette.text.secondary,
                                    marginBottom: theme.spacing(1),
                                    flexShrink: 0,
                                }}
                            >
                                Applied to all new members
                            </Container>
                            <div
                                style={{
                                    overflowY: 'auto',
                                    maxHeight: 'calc(90vh - 260px)',
                                }}
                            >
                                {permissionRoles &&
                                permissionRoles.length > 0 ? (
                                    renderPermissionRoles(permissionRoles)
                                ) : (
                                    <span
                                        style={{
                                            fontSize: 13,
                                            color: theme.palette.text.secondary,
                                            fontStyle: 'italic',
                                        }}
                                    >
                                        No permission roles found.
                                    </span>
                                )}
                            </div>
                        </Container>
                    </Container>

                    <Divider
                        style={{
                            margin: theme.spacing(1, 3, 0, 3),
                            flexShrink: 0,
                        }}
                    />

                    {/* ── Footer ── */}
                    <Container
                        style={{
                            padding: theme.spacing(2, 3),
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            flexShrink: 0,
                        }}
                    >
                        {successRows.length > 0 && (
                            <span
                                style={{
                                    fontSize: 13,
                                    color: '#008C85',
                                    fontWeight: 500,
                                }}
                            >
                                {successRows.length} of {rows.length} created
                            </span>
                        )}
                        <div style={{ flex: 1 }} />
                        <Button
                            variant="outlined"
                            onClick={onClose}
                            disabled={isBulkLoading}
                            style={{
                                textTransform: 'none',
                                marginRight: theme.spacing(2),
                            }}
                        >
                            Cancel
                        </Button>
                        <Tooltip
                            title={
                                !canEditUsers
                                    ? 'You do not have permission to add users'
                                    : ''
                            }
                        >
                            <span>
                                <Button
                                    variant="contained"
                                    onClick={handleCreate}
                                    disabled={
                                        isBulkLoading ||
                                        pendingRows.length === 0 ||
                                        !canEditUsers
                                    }
                                    style={{
                                        textTransform: 'none',
                                        backgroundColor:
                                            isBulkLoading ||
                                            pendingRows.length === 0 ||
                                            !canEditUsers
                                                ? undefined
                                                : '#008C85',
                                        color:
                                            isBulkLoading ||
                                            pendingRows.length === 0 ||
                                            !canEditUsers
                                                ? undefined
                                                : 'white',
                                        minWidth: 160,
                                    }}
                                >
                                    {isBulkLoading ? (
                                        <CircularProgress
                                            size={22}
                                            style={{ color: 'white' }}
                                        />
                                    ) : (
                                        `Create ${pendingRows.length} Member${
                                            pendingRows.length !== 1 ? 's' : ''
                                        }`
                                    )}
                                </Button>
                            </span>
                        </Tooltip>
                    </Container>

                    {/* Hidden file input */}
                    <input
                        ref={uploadRef}
                        type="file"
                        accept=".csv"
                        style={{ display: 'none' }}
                        onChange={handleUpload}
                    />
                </Paper>
            </Slide>
        </Modal>
    )
}
